import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { projectCategories, projects, type ProjectCategory } from '../data/portfolioProjects';
import { ProjectFrame } from '../components/portfolio/ProjectFrame';
import { pageMeta, useDocumentMeta } from '../hooks/useDocumentMeta';
type Filter = ProjectCategory | 'All work';
const filters: Filter[] = ['All work', ...projectCategories];
const PAGE_SIZE = 12;
export function Portfolio() {
  useDocumentMeta(pageMeta.portfolio);
  const [filter, setFilter] = useState<Filter>('All work');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const shown = useMemo(() => filter === 'All work' ? projects : projects.filter(p => p.category === filter), [filter]);
  const page = shown.slice(0, visible);
  const changeFilter = (next: Filter) => {
    setFilter(next);
    setVisible(PAGE_SIZE);
  };
  return <>
      <section className="relative overflow-hidden bg-ink pb-12 pt-12 lg:pb-14 lg:pt-16" data-id="element-386" data-mp="16a3qouw57y1f">
        <div className="absolute inset-0 bp opacity-80" aria-hidden="true" data-id="element-387" data-mp="1b96n5qy24vxs" />
        <div className="absolute -right-40 -top-32 h-[36rem] w-[36rem] rounded-full bg-navy-700/35 blur-[130px]" aria-hidden="true" data-id="element-388" data-mp="1b9gilu1vfvav" />

        <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-389" data-mp="1kyq955h2h5wv">
          <p className="font-mono text-[11.5px] uppercase tracking-[0.18em] text-forge-cyan" data-id="element-390" data-mp="1l0t84f0769dq">Selected client work</p>
          <h1 className="mt-3 max-w-3xl font-display text-[2rem] font-extrabold leading-[1.08] tracking-[-0.02em] text-white sm:text-[2.5rem]" data-id="element-391" data-mp="1tzelzwuq5086">
            Custom websites and systems built around real operating needs.
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-mist-200" data-id="element-392" data-mp="23wt3lzsdvv22">
            A selection of ecommerce, service, membership, multi-location, and custom-functionality projects. Each one was
            shaped around a different customer journey, catalog, sales process, or operational requirement.
          </p>

          <div className="mt-8 flex flex-wrap gap-2" role="group" aria-label="Filter work by type" data-id="element-393" data-mp="1jf2932pitsr">
            {filters.map(f => <button key={f} type="button" onClick={() => changeFilter(f)} aria-pressed={filter === f} className={`rounded-xl border px-4 py-2.5 text-[13px] font-semibold transition-colors ${filter === f ? 'border-forge-orange bg-forge-orange/15 text-white' : 'border-white/12 bg-white/[0.03] text-mist-200 hover:border-white/30'}`} data-id="element-394" data-mp="bh1m83yv5bjl">
                {f}
              </button>)}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy-900 py-12 lg:py-16" data-id="element-395" data-mp="145gxqgisdbq6">
        <div className="absolute inset-0 dots opacity-[0.25]" aria-hidden="true" data-id="element-396" data-mp="z6vxwi7g4u29" />
        <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-397" data-mp="z4szb9s89slx">
          <p aria-live="polite" className="mb-6 font-mono text-[12px] uppercase tracking-[0.14em] text-mist-400" data-id="element-398" data-mp="1it75yg6kzzb4">
            Showing {page.length} of {shown.length} {shown.length === 1 ? 'project' : 'projects'}
            {filter !== 'All work' && ` · ${filter}`}
          </p>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-399" data-mp="1dsjksb51acot">
            {page.map((project, i) => <motion.article key={project.name} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            delay: Math.min(i % PAGE_SIZE, 8) * 0.04
          }} className="min-w-0 overflow-hidden rounded-2xl border border-white/10 bg-navy-950/70" data-id="element-400" data-mp="z3wuxfhs62jc" data-mpi={i}>
                <ProjectFrame project={project} data-id="element-401" data-mp="g5c7pyj0fhaz" />
                <div className="p-6" data-id="element-402" data-mp="g7x9knjb5x8q">
                  <p className="font-mono text-[11.5px] uppercase tracking-[0.14em] text-mist-400" data-id="element-403" data-mp="l5gmnk49s3z3">{project.category}</p>
                  <h2 className="mt-2 font-display text-[16.5px] font-bold leading-snug text-white" data-id="element-404" data-mp="1oet3dtnrqmwz">{project.name}</h2>
                  <p className="mt-2.5 text-[13.5px] leading-relaxed text-mist-200" data-id="element-405" data-mp="1jh9rv1iggo8q">
                    <span className="font-semibold text-white" data-id="element-406" data-mp="1eozvp3qqyry0">Built to support: </span>
                    {project.supports}
                  </p>
                  {project.url && <a href={project.url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-forge-ember" data-id="element-407" data-mp="14s3rvbbsmtzs">
                      Visit site
                      <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-408" data-mp="qt44e700ol57" />
                    </a>}
                </div>
              </motion.article>)}
          </div>

          {visible < shown.length && <div className="mt-8 flex justify-center" data-id="element-409" data-mp="2l4aj71ggtpce">
              <button type="button" onClick={() => setVisible(v => v + PAGE_SIZE)} className="rounded-xl border border-white/15 px-6 py-3.5 text-[14.5px] font-semibold text-white transition-colors hover:border-forge-orange/60 hover:bg-white/5" data-id="element-410" data-mp="2souvytei8ts1">
                Load more projects
              </button>
            </div>}

          <div className="mt-12 flex flex-col items-start gap-5 rounded-2xl bg-ink px-6 py-8 lg:flex-row lg:items-center lg:justify-between lg:px-9" data-id="element-411" data-mp="29rtlwu63g3h8">
            <p className="max-w-3xl text-[14.5px] leading-relaxed text-mist-200" data-id="element-412" data-mp="2joy1gb5fp0p2">
              Have a project with similar complexity? Tell us what the business needs the site to support. We will explain the
              right next step and scope the work before it begins.
            </p>
            <Link to="/#get-started" className="group inline-flex shrink-0 items-center gap-2 rounded-xl bg-forge-orange px-5 py-3.5 text-[14.5px] font-bold text-ink transition-transform hover:-translate-y-0.5" data-id="element-413" data-mp="3v6fw8l51epzg">
              Discuss Your Project
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-414" data-mp="1v3ya57tszm6d" />
            </Link>
          </div>
        </div>
      </section>
    </>;
}